import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useHomepageSections } from '../hooks/usePublicContent';
import { placeholderImage } from './ui/EditorialImage';
import { RatingStars } from './ui/RatingStars';
// Container with optional inline style support
function Wrap({ children, style, className = '' }: { children: React.ReactNode; style?: React.CSSProperties; className?: string }) {
  return <div style={style} className={`mx-auto w-full max-w-[1400px] px-6 md:px-10 ${className}`}>{children}</div>;
}

// ─── Types (mirrors admin HeroConfig) ────────────────────────────────────────

type LayoutKey =
  | 'classic' | 'split' | 'centered' | 'minimal' | 'editorial'
  | 'slider' | 'fullscreen' | 'video' | 'mosaic' | 'diagonal' | 'parallax';
type FontKey = 'sans' | 'serif' | 'display' | 'mono';
type BtnStyleKey = 'filled' | 'outline' | 'ghost' | 'pill';
type TextAlign = 'left' | 'center' | 'right';
type BlockType = 'h1' | 'h2' | 'h3' | 'p' | 'button' | 'badge' | 'divider';

interface ContentBlock {
  id: string;
  type: BlockType;
  text: string;
  link?: string;
}

interface Slide {
  id: string;
  image: string;
  mobile_image?: string;
  blocks: ContentBlock[];
}

interface HeroConfig {
  layout?: LayoutKey;
  image?: string;
  mobile_image?: string;
  blocks?: ContentBlock[];
  image_side?: 'left' | 'right';
  overlay_opacity?: number;
  accent_color?: string;
  slides?: Slide[];
  autoplay?: boolean;
  autoplay_speed?: number;
  show_dots?: boolean;
  show_arrows?: boolean;
  video_url?: string;
  images?: string[];
  scroll_indicator?: boolean;
  diagonal_direction?: 'left' | 'right';
  font_family?: FontKey;
  headline_color?: string;
  headline_size_px?: number;
  text_align?: TextAlign;
  text_shadow?: boolean;
  button_style?: BtnStyleKey;
  button_color?: string;
  button_text_color?: string;
}

// ─── Style helpers ────────────────────────────────────────────────────────────

const FONT_MAP: Record<FontKey, string> = {
  sans: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  serif: 'Georgia, "Palatino Linotype", "Book Antiqua", serif',
  display: 'Impact, "Haettenschweiler", "Arial Black", sans-serif',
  mono: '"Courier New", "Lucida Console", monospace',
};

function buildStyles(cfg: HeroConfig, darkBg: boolean) {
  const px  = cfg.headline_size_px ?? 48;
  const bc  = cfg.button_color ?? '#111827';
  const btc = cfg.button_text_color ?? '#ffffff';
  const ff  = FONT_MAP[cfg.font_family ?? 'sans'];
  const ta  = (cfg.text_align ?? 'left') as React.CSSProperties['textAlign'];
  const shadow = cfg.text_shadow ? { textShadow: '0 2px 16px rgba(0,0,0,0.7)' } : {};
  const color = cfg.headline_color ?? (darkBg ? '#ffffff' : '#111827');

  const h1: React.CSSProperties = {
    fontFamily: ff, fontSize: `clamp(${Math.round(px * 0.6)}px, ${px * 0.8}vw, ${px}px)`,
    fontWeight: px >= 56 ? 900 : px >= 40 ? 800 : px >= 28 ? 700 : 600,
    lineHeight: 1.1, textAlign: ta, color, ...shadow,
  };
  const h2: React.CSSProperties = { ...h1, fontSize: `clamp(${Math.round(px * 0.5)}px, ${Math.round(px * 0.6)}vw, ${Math.round(px * 0.72)}px)`, fontWeight: 700 };
  const h3: React.CSSProperties = { ...h1, fontSize: `clamp(${Math.round(px * 0.4)}px, ${Math.round(px * 0.5)}vw, ${Math.round(px * 0.58)}px)`, fontWeight: 600 };
  const p: React.CSSProperties  = { fontFamily: ff, fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)', lineHeight: 1.7, textAlign: ta, color: darkBg ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.6)' };

  const btnBase: React.CSSProperties = {
    fontFamily: ff, fontSize: '0.85rem', fontWeight: 600,
    padding: '12px 28px', display: 'inline-block', textDecoration: 'none', transition: 'opacity 0.15s',
  };
  const btn: React.CSSProperties = (() => {
    switch (cfg.button_style ?? 'filled') {
      case 'outline': return { ...btnBase, border: `2px solid ${bc}`, color: bc, background: 'transparent', borderRadius: 6 };
      case 'ghost':   return { ...btnBase, color: btc, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6 };
      case 'pill':    return { ...btnBase, background: bc, color: btc, border: 'none', borderRadius: 9999 };
      default:        return { ...btnBase, background: bc, color: btc, border: 'none', borderRadius: 6 };
    }
  })();

  const alignItems = ta === 'center' ? 'center' : ta === 'right' ? 'flex-end' : 'flex-start';

  return { h1, h2, h3, p, btn, ff, ta, alignItems };
}

// ─── Block renderer ───────────────────────────────────────────────────────────

function Blocks({ blocks, cfg, darkBg = true }: { blocks: ContentBlock[]; cfg: HeroConfig; darkBg?: boolean }) {
  const s = buildStyles(cfg, darkBg);
  const { alignItems, ta } = s;

  const muteColor = darkBg ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.58)';
  const badgeBg   = darkBg ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.07)';
  const badgeClr  = darkBg ? '#fff' : '#111';
  const divClr    = darkBg ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems, width: '100%' }}>
      {blocks.map(b => {
        switch (b.type) {
          case 'h1': return <h1 key={b.id} style={{ ...s.h1, margin: 0 }}>{b.text}</h1>;
          case 'h2': return <h2 key={b.id} style={{ ...s.h2, margin: 0 }}>{b.text}</h2>;
          case 'h3': return <h3 key={b.id} style={{ ...s.h3, margin: 0 }}>{b.text}</h3>;
          case 'p':  return <p  key={b.id} style={{ ...s.p, margin: 0, color: muteColor, width: '100%', textAlign: ta as React.CSSProperties['textAlign'] }}>{b.text}</p>;
          case 'button': return (
            <div key={b.id}>
              {b.link ? (
                <Link to={b.link} style={s.btn}>{b.text}</Link>
              ) : (
                <span style={s.btn}>{b.text}</span>
              )}
            </div>
          );
          case 'badge': return (
            <div key={b.id}>
              <span style={{
                display: 'inline-block', background: badgeBg, color: badgeClr,
                borderRadius: 9999, padding: '4px 14px',
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>{b.text}</span>
            </div>
          );
          case 'divider': return (
            <div key={b.id} style={{ borderTop: `1px solid ${divClr}`, width: '100%' }}/>
          );
          default: return null;
        }
      })}
    </div>
  );
}

// ─── Image helpers ────────────────────────────────────────────────────────────

const BASE = import.meta.env.VITE_API_URL ?? '';

function imgSrc(path: string | undefined) {
  if (!path) return null;
  return path.startsWith('/') ? `${BASE}${path}` : path;
}

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return mobile;
}

function HeroImg({ cfg, className }: { cfg: HeroConfig; className?: string }) {
  const isMob = useIsMobile();
  const src = imgSrc((isMob && cfg.mobile_image) ? cfg.mobile_image : cfg.image);
  if (!src) return null;
  return <img src={src} alt="" className={className} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>;
}

// ─── Layout renderers ─────────────────────────────────────────────────────────

function HeroCms({ cfg }: { cfg: HeroConfig }) {
  const blocks  = cfg.blocks ?? [];
  const overlay = cfg.overlay_opacity ?? 0.45;
  const ff      = FONT_MAP[cfg.font_family ?? 'sans'];
  const layout  = cfg.layout ?? 'classic';

  if (layout === 'slider') return <SliderLayout cfg={cfg}/>;
  if (layout === 'split')  return <SplitLayout cfg={cfg}/>;
  if (layout === 'centered') return <CenteredLayout cfg={cfg}/>;
  if (layout === 'minimal')  return <MinimalLayout cfg={cfg}/>;

  // Dark full-bleed layouts: classic, fullscreen, parallax, video, diagonal, editorial
  const minH = layout === 'fullscreen' ? '100vh' : '560px';
  const isParallax = layout === 'parallax';
  const isDiag = layout === 'diagonal';
  const isEditorial = layout === 'editorial';

  if (isDiag) return <DiagonalLayout cfg={cfg}/>;
  if (isEditorial) return <EditorialLayout cfg={cfg}/>;

  return (
    <section style={{ position: 'relative', overflow: 'hidden', minHeight: minH, display: 'flex', alignItems: 'center', fontFamily: ff }}>
      <HeroImg cfg={cfg} className=""/>
      {isParallax
        ? <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, rgba(28,37,65,0.95) 0%, rgba(28,37,65,0.6) 50%, rgba(28,37,65,0.2) 100%)` }}/>
        : <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${overlay})` }}/>}
      <Wrap style={{ position: 'relative', zIndex: 1, paddingTop: '5rem', paddingBottom: '5rem' }}>
        {blocks.length > 0 ? <Blocks blocks={blocks} cfg={cfg} darkBg={true}/> : <FallbackContent cfg={cfg} dark/>}
      </Wrap>
      {layout === 'fullscreen' && cfg.scroll_indicator !== false && (
        <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', opacity: 0.55 }}>
          <div style={{ width: 22, height: 36, borderRadius: 11, border: '2px solid white', display: 'flex', justifyContent: 'center', paddingTop: 6 }}>
            <div style={{ width: 3, height: 8, borderRadius: 2, background: 'white', animation: 'bounce 1.5s infinite' }}/>
          </div>
        </div>
      )}
    </section>
  );
}

function SliderLayout({ cfg }: { cfg: HeroConfig }) {
  const slides = cfg.slides ?? [];
  const [active, setActive] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMob = useIsMobile();
  const ff = FONT_MAP[cfg.font_family ?? 'sans'];

  useEffect(() => {
    if (!cfg.autoplay || slides.length < 2) return;
    timer.current = setInterval(() => setActive(p => (p + 1) % slides.length), cfg.autoplay_speed ?? 5000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [cfg.autoplay, cfg.autoplay_speed, slides.length]);

  if (!slides.length) return <HeroCms cfg={{ ...cfg, layout: 'classic' }}/>;

  const slide = slides[active];
  const si = imgSrc((isMob && slide.mobile_image) ? slide.mobile_image : slide.image);

  return (
    <section style={{ position: 'relative', overflow: 'hidden', minHeight: '560px', fontFamily: ff }}>
      {si && <img src={si} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.5s' }}/>}
      <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${cfg.overlay_opacity ?? 0.5})` }}/>
      <Wrap style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', minHeight: '560px', paddingTop: '5rem', paddingBottom: '5rem' }}>
        <div style={{ width: '100%' }}>
          {slide.blocks?.length ? <Blocks blocks={slide.blocks} cfg={cfg} darkBg={true}/> : <FallbackContent cfg={cfg} dark/>}
        </div>
      </Wrap>
      {cfg.show_arrows !== false && slides.length > 1 && (
        <>
          <button onClick={() => setActive(p => (p - 1 + slides.length) % slides.length)}
            style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ‹
          </button>
          <button onClick={() => setActive(p => (p + 1) % slides.length)}
            style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ›
          </button>
        </>
      )}
      {cfg.show_dots !== false && slides.length > 1 && (
        <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8, zIndex: 10 }}>
          {slides.map((_, i) => (
            <button key={i} onClick={() => setActive(i)}
              style={{ width: i === active ? 24 : 8, height: 8, borderRadius: 4, background: i === active ? 'white' : 'rgba(255,255,255,0.4)', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}/>
          ))}
        </div>
      )}
    </section>
  );
}

function SplitLayout({ cfg }: { cfg: HeroConfig }) {
  const side = cfg.image_side ?? 'left';
  const isMob = useIsMobile();
  const blocks = cfg.blocks ?? [];
  const src = imgSrc((isMob && cfg.mobile_image) ? cfg.mobile_image : cfg.image);

  const content = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'clamp(2rem, 5vw, 5rem)', fontFamily: FONT_MAP[cfg.font_family ?? 'sans'] }}>
      {blocks.length ? <Blocks blocks={blocks} cfg={cfg} darkBg={false}/> : <FallbackContent cfg={cfg} dark={false}/>}
    </div>
  );
  const image = (
    <div style={{ flex: 1, minHeight: 400, position: 'relative', overflow: 'hidden' }}>
      {src
        ? <img src={src} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>
        : <div style={{ position: 'absolute', inset: 0, background: '#d1cdc7' }}/>}
    </div>
  );

  return (
    <section style={{ display: 'flex', flexWrap: 'wrap', minHeight: '520px' }}>
      {side === 'left' ? <>{image}{content}</> : <>{content}{image}</>}
    </section>
  );
}

function CenteredLayout({ cfg }: { cfg: HeroConfig }) {
  const blocks = cfg.blocks ?? [];
  const overlay = cfg.overlay_opacity ?? 0.5;

  return (
    <section style={{ position: 'relative', overflow: 'hidden', minHeight: '560px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <HeroImg cfg={cfg} className=""/>
      <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${overlay})` }}/>
      <div style={{ position: 'relative', zIndex: 1, background: 'rgba(255,255,255,0.95)', maxWidth: 680, margin: '3rem', padding: '3rem', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {blocks.length ? <Blocks blocks={blocks} cfg={cfg} darkBg={false}/> : <FallbackContent cfg={cfg} dark={false}/>}
      </div>
    </section>
  );
}

function MinimalLayout({ cfg }: { cfg: HeroConfig }) {
  const blocks = cfg.blocks ?? [];
  const ff = FONT_MAP[cfg.font_family ?? 'sans'];

  return (
    <section style={{ background: '#fafaf8', borderBottom: '1px solid #e5e5e0', fontFamily: ff }}>
      <Wrap style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
        {blocks.length ? <Blocks blocks={blocks} cfg={cfg} darkBg={false}/> : <FallbackContent cfg={cfg} dark={false}/>}
      </Wrap>
    </section>
  );
}

function DiagonalLayout({ cfg }: { cfg: HeroConfig }) {
  const dir = cfg.diagonal_direction ?? 'left';
  const blocks = cfg.blocks ?? [];
  const src = imgSrc(cfg.image);
  const clip = dir === 'left'
    ? 'polygon(0 0, 62% 0, 50% 100%, 0 100%)'
    : 'polygon(38% 0, 100% 0, 100% 100%, 50% 100%)';

  return (
    <section style={{ position: 'relative', overflow: 'hidden', minHeight: '520px', display: 'flex' }}>
      {src && (
        <div style={{ position: 'absolute', inset: 0, clipPath: clip }}>
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
        </div>
      )}
      <div style={{
        marginLeft: dir === 'left' ? 'auto' : 0,
        marginRight: dir === 'left' ? 0 : 'auto',
        width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '4rem clamp(2rem, 5vw, 4rem)', position: 'relative', zIndex: 1,
        fontFamily: FONT_MAP[cfg.font_family ?? 'sans'],
      }}>
        {blocks.length ? <Blocks blocks={blocks} cfg={cfg} darkBg={false}/> : <FallbackContent cfg={cfg} dark={false}/>}
      </div>
    </section>
  );
}

function EditorialLayout({ cfg }: { cfg: HeroConfig }) {
  const blocks  = cfg.blocks ?? [];
  const accent  = cfg.accent_color ?? '#111827';
  const src     = imgSrc(cfg.image);

  return (
    <section style={{ background: '#0f0f0f', display: 'flex', minHeight: '560px', overflow: 'hidden' }}>
      <div style={{ flex: '0 0 55%', position: 'relative' }}>
        {src
          ? <img src={src} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.65 }}/>
          : <div style={{ position: 'absolute', inset: 0, background: '#1a1a1a' }}/>}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.3), transparent)' }}/>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3rem 2.5rem', fontFamily: FONT_MAP[cfg.font_family ?? 'sans'] }}>
        {blocks.length ? <Blocks blocks={blocks} cfg={cfg} darkBg={true}/> : <FallbackContent cfg={cfg} dark accentColor={accent}/>}
      </div>
    </section>
  );
}

// ─── Fallback (hardcoded for when no CMS config is found) ────────────────────

function FallbackContent({ dark, accentColor }: { cfg?: HeroConfig; dark: boolean; accentColor?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <h1 style={{ color: dark ? '#fff' : '#111', fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 700, margin: 0, lineHeight: 1.1 }}>
        Premium art, framed to last.
      </h1>
      <p style={{ color: dark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.6)', fontSize: '1.05rem', margin: 0, lineHeight: 1.7 }}>
        Archival prints in solid-wood frames. Free shipping over $75.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        <Link to="/categories/wall-art" style={{ background: accentColor ?? (dark ? '#fff' : '#111'), color: accentColor ? '#fff' : (dark ? '#111' : '#fff'), padding: '12px 28px', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none', display: 'inline-block' }}>
          Shop Now
        </Link>
      </div>
    </div>
  );
}

// ─── Public entry point ───────────────────────────────────────────────────────

export function HeroSection() {
  const { data: sections, isLoading } = useHomepageSections();

  // Hold render during initial fetch so the CMS hero never flashes HeroClassic first
  if (isLoading) {
    return <div style={{ minHeight: 560, background: '#0f0f0f' }}/>;
  }

  const hero = sections?.find(s => s.type === 'hero_banner' && s.is_visible !== false);
  const cfg = hero?.config as HeroConfig | undefined;

  if (cfg?.layout) return <HeroCms cfg={cfg}/>;

  // Sections loaded but no hero_banner configured — fall back to the default
  return <HeroClassic/>;
}

// ─── Original hardcoded fallback ──────────────────────────────────────────────

function HeroClassic() {
  return (
    <section className="relative flex min-h-[600px] w-full items-center overflow-hidden bg-ink md:min-h-[640px]">
      <div className="absolute inset-0">
        <img
          src={placeholderImage('framed-hero-living-room', 2400, 1600)}
          alt="A framed art print hanging above a couch in a sunlit living room"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/45 to-ink/10" />
      <Wrap className="relative py-20">
        <span className="inline-block bg-clay px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-cream">
          Summer Sale — Up to 25% Off
        </span>
        <h1 className="text-balance mt-6 max-w-xl font-serif text-5xl font-light leading-[1.05] text-cream sm:text-6xl md:text-7xl">
          Premium art, framed to last.
        </h1>
        <p className="mt-5 max-w-md text-base leading-relaxed text-cream/80">
          Archival prints in solid-wood frames. Trusted by 12,000+ customers. Free shipping over $75.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link to="/categories/wall-art" className="bg-cream px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.1em] text-ink transition-colors hover:bg-clay hover:text-cream">
            Shop Best Sellers
          </Link>
          <Link to="/custom-prints" className="border border-cream/70 px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.1em] text-cream transition-colors hover:bg-cream hover:text-ink">
            Upload Your Photo
          </Link>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <RatingStars value={4.8} dark />
          <span className="text-xs text-cream/70">Rated 4.8/5 from 2,400+ reviews</span>
        </div>
      </Wrap>
    </section>
  );
}
